(() => {
  if (customElements.get('ggx-living-world')) return;
  const core = window.GGXLivingWorldsCore;
  if (!core) return;
  const subscribers = new Set();
  let boundaryTimer;
  let globalAbort;
  let motionPaused = false;
  let controlSequence = 0;
  try { motionPaused = window.sessionStorage.getItem('ggx-living-motion-paused') === 'true'; } catch { }
  function setMotionPaused(value) {
    motionPaused = value;
    try { window.sessionStorage.setItem('ggx-living-motion-paused', String(value)); } catch { }
    for (const world of subscribers) world.updateMotion();
    window.dispatchEvent(new CustomEvent('ggx:motion-preference', { detail: { paused: value } }));
  }
  function refreshAll() {
    clearTimeout(boundaryTimer);
    for (const world of subscribers) world.refresh();
    if (!subscribers.size || document.hidden) return;
    try { boundaryTimer = setTimeout(refreshAll, Math.max(1, core.nextBoundary() - Date.now())); } catch { }
  }
  function subscribe(world) {
    if (!subscribers.size) {
      globalAbort = new AbortController();
      const options = { signal: globalAbort.signal };
      document.addEventListener('visibilitychange', refreshAll, options);
      window.addEventListener('pageshow', refreshAll, options);
      window.addEventListener('focus', refreshAll, options);
      window.addEventListener('pagehide', () => { clearTimeout(boundaryTimer); for (const item of subscribers) item.pause(); }, options);
    }
    subscribers.add(world);
    refreshAll();
  }
  function unsubscribe(world) {
    subscribers.delete(world);
    if (!subscribers.size) { clearTimeout(boundaryTimer); globalAbort?.abort(); globalAbort = null; }
  }
  class LivingWorld extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      core.initialMount(this);
      this.abort = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.inView = false;
      this.viewportQuery = matchMedia('(max-width: 749px)');
      this.viewportQuery.addEventListener('change', () => {
        this.generation += 1;
        this.pendingState = null;
        this.querySelectorAll('.ggx-living-preload').forEach(node => node.remove());
        if (!this.mobileFailed && this.failedViewport !== this.viewportQuery.matches) {
          delete this.dataset.fallback;
          this.failedState = null;
        }
        this.alignEffects();
        this.refresh();
      }, { signal: this.abort.signal });
      this.contrastQuery = matchMedia('(forced-colors: active)');
      this.contrastQuery.addEventListener('change', () => this.updateMotion(), { signal: this.abort.signal });
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener('change', () => this.updateMotion(), { signal: this.abort.signal });
      this.motionControl = this.querySelector('[data-living-control]');
      this.motionButton = this.motionControl?.querySelector('button');
      this.motionStatus = this.motionControl?.querySelector('[data-living-status]');
      if (this.motionButton && this.motionStatus) {
        this.motionStatus.id ||= `ggx-living-status-${++controlSequence}`;
        this.motionButton.setAttribute('aria-describedby', this.motionStatus.id);
      }
      this.motionButton?.addEventListener('click', () => {
        if (this.motionButton.disabled) return;
        if (this.videoMotion?.selected() && this.videoMotion.blocked) { this.videoMotion.manualPlay(); return; }
        setMotionPaused(!motionPaused);
      }, { signal: this.abort.signal });
      this.motionSupported = typeof IntersectionObserver === 'function' && typeof ResizeObserver === 'function' && typeof this.querySelector('[data-living-effects]')?.getAnimations === 'function';
      this.videoMotion = window.GGXMobileSceneVideo ? new window.GGXMobileSceneVideo(this, () => this.updateMotion()) : null;
      document.addEventListener('load', event => {
        if (event.target.matches?.('link[rel="stylesheet"]')) this.updateMotion();
      }, { capture: true, signal: this.abort.signal });
      this.addEventListener('load', () => { this.alignEffects(); this.updateMotion(); }, { capture: true, signal: this.abort.signal });
      this.addEventListener('error', event => this.onImageError(event), { capture: true, signal: this.abort.signal });
      if (this.motionSupported) {
        this.resizeObserver = new ResizeObserver(() => { this.alignEffects(); this.updateMotion(); });
        this.resizeObserver.observe(this);
        this.intersectionObserver = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          this.inView = entry.isIntersecting && entry.intersectionRatio > 0;
          this.updateMotion();
        }, { threshold: [0, 0.01] });
        this.intersectionObserver.observe(this);
      }
      const initialImage = this.querySelector('[data-living-frame] img');
      if (initialImage?.complete && !initialImage.naturalWidth) this.onImageError({ target: initialImage });
      subscribe(this);
      this.alignEffects();
    }
    disconnectedCallback() {
      this.generation += 1;
      this.pendingState = null;
      this.querySelectorAll('.ggx-living-preload').forEach(node => node.remove());
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.pause();
      this.videoMotion?.destroy();
      unsubscribe(this);
    }
    async refresh() {
      if (this.videoMotion && !document.hidden && this.isConnected) this.videoMotion.suspended = false;
      this.updateMotion();
      if (document.hidden || this.mobileFailed) return;
      const state = core.requestedState(this);
      if (this.dataset.fallback === 'true' && this.failedState !== state) {
        delete this.dataset.fallback;
        this.failedState = null;
        this.failedViewport = null;
        this.updateMotion();
      }
      if (this.failedState === state) return;
      if (this.dataset.state === state || this.pendingState === state) return;
      const template = this.querySelector(`template[data-living-base="${state}"]`);
      if (!template) return;
      const generation = ++this.generation;
      this.pendingState = state;
      const fragment = template.content.cloneNode(true);
      const image = fragment.querySelector('img');
      const staging = document.createElement('div');
      staging.className = 'ggx-living-preload';
      staging.append(fragment);
      this.append(staging);
      try {
        if (image) await image.decode();
        if (!this.isConnected || this.mobileFailed || generation !== this.generation || core.requestedState(this) !== state) return;
        const frame = this.querySelector('[data-living-frame]');
        frame.replaceChildren(...staging.childNodes);
        this.dataset.state = state;
        this.alignEffects();
      } catch {
        if (generation === this.generation) {
          this.failedState = state;
          this.failedViewport = this.viewportQuery.matches;
          this.dataset.fallback = 'true';
          this.pause();
        }
      }
      finally { staging.remove(); if (generation === this.generation) this.pendingState = null; this.updateMotion(); }
    }
    onImageError(event) {
      if (!event.target.matches('[data-living-frame] img')) return;
      if (this.dataset.fallback === 'true') {
        if (this.dataset.mobile !== 'true') return;
        const desktop = this.querySelector('template[data-living-desktop-original]');
        if (!desktop) return;
        this.mobileFailed = true;
        this.generation += 1;
        this.pendingState = null;
        this.querySelectorAll('.ggx-living-preload').forEach(node => node.remove());
        this.dataset.mobile = 'false';
        this.querySelector('[data-living-frame]').replaceChildren(desktop.content.cloneNode(true));
        this.pause();
        this.alignEffects();
        return;
      }
      const template = this.querySelector('template[data-living-original]');
      if (!template) return;
      this.failedState = this.dataset.state;
      this.failedViewport = this.viewportQuery.matches;
      this.dataset.fallback = 'true';
      this.dataset.state = 'day';
      this.querySelector('[data-living-frame]').replaceChildren(template.content.cloneNode(true));
      this.pause();
      this.alignEffects();
    }
    alignEffects() {
      const image = this.querySelector('[data-living-frame] img');
      const plane = this.querySelector('[data-living-effects]');
      if (!image || !plane || !image.naturalWidth) return;
      const style = getComputedStyle(image);
      const position = style.objectPosition.split(' ');
      const fraction = (value, fallback) => value?.endsWith('%') ? parseFloat(value) / 100 : fallback;
      const imageRect = image.getBoundingClientRect();
      const hostRect = this.getBoundingClientRect();
      const source = core.sourceDimensions(this, this.viewportQuery.matches);
      const geometry = core.fitPlane({ width: imageRect.width, height: imageRect.height, sourceWidth: source.width || image.naturalWidth, sourceHeight: source.height || image.naturalHeight, fit: style.objectFit === 'contain' ? 'contain' : 'cover', positionX: fraction(position[0], .5), positionY: fraction(position[1], .5) });
      if (!geometry) return;
      Object.assign(plane.style, { width: `${geometry.width}px`, height: `${geometry.height}px`, left: `${geometry.left + imageRect.left - hostRect.left}px`, top: `${geometry.top + imageRect.top - hostRect.top}px` });
    }
    updateMotion() {
      // A pending image decode can settle after disconnectedCallback destroys media.
      if (!this.isConnected) return;
      const videoState = this.videoMotion?.update(motionPaused);
      if (videoState) {
        for (const animation of this.animationHandles || []) { try { animation.pause(); } catch { } }
        this.dataset.running = String(videoState.active);
        this.dataset.animationCount = videoState.active ? '1' : '0';
        this.updateVideoControl(videoState);
        return;
      }
      const image = this.querySelector('[data-living-frame] img');
      const eligible = image?.complete && image.naturalWidth > 0 && this.isConnected && !this.motionQuery?.matches && !this.contrastQuery?.matches && this.dataset.motion !== 'false' && this.dataset.fallback !== 'true' && !this.mobileFailed && this.dataset.videoFailure !== 'true' && this.motionSupported;
      const visible = this.inView && !document.hidden;
      const plane = this.querySelector('[data-living-effects]');
      if (eligible && visible && !this.dataset.effectsLoaded) {
        const template = this.querySelector('template[data-living-effect-assets]');
        if (template && plane) {
          plane.append(template.content.cloneNode(true));
          this.dataset.effectsLoaded = 'true';
          this.alignEffects();
        }
      }
      let handles = [];
      try { handles = plane?.getAnimations({ subtree: true }) || []; } catch { }
      for (const animation of this.animationHandles || []) {
        if (!handles.includes(animation)) { try { animation.pause(); } catch { } }
      }
      this.animationHandles = handles;
      let active = Boolean(eligible && visible && !motionPaused && handles.length);
      try {
        for (const animation of handles) {
          if (active) { if (animation.playState !== 'running') animation.play(); }
          else animation.pause();
        }
        active = active && handles.every(animation => animation.playState === 'running');
      } catch { active = false; }
      if (!active) for (const animation of handles) { try { animation.pause(); } catch { } }
      this.motionEmpty = Boolean(eligible && visible && !handles.length);
      this.motionFailed = Boolean(eligible && visible && !motionPaused && handles.length && !active);
      this.dataset.animationCount = String(handles.length);
      this.dataset.running = String(active);
      this.updateMotionControl(image, active);
    }
    updateVideoControl({ active, reason }) {
      if (!this.motionControl || !this.motionButton || !this.motionStatus) return;
      const labels = this.motionControl.dataset;
      this.motionButton.disabled = reason === 'idle';
      this.motionButton.textContent = reason === 'idle' ? labels.idle_button : reason === 'paused' || reason === 'video_blocked' ? labels.play : labels.pause;
      this.motionButton.setAttribute('aria-label', this.motionButton.textContent);
      this.motionButton.setAttribute('title', this.motionButton.textContent);
      this.motionStatus.textContent = labels[reason];
      this.motionControl.dataset.reason = reason;
      this.motionControl.hidden = false;
    }
    updateMotionControl(image, active) {
      if (!this.motionControl || !this.motionButton || !this.motionStatus) return;
      const labels = this.motionControl.dataset;
      // A registered Diner portrait with every mobile effect explicitly off is intentional.
      const mobileEffects = this.dataset.world === 'diner' && this.dataset.mobile === 'true' && this.viewportQuery?.matches
        ? Array.from(this.querySelector('template[data-living-effect-assets]')?.content.querySelectorAll('[data-mobile-enabled]') || []) : [];
      const intentionallyStillMobile = mobileEffects.length > 0 && mobileEffects.every(effect => effect.dataset.mobileEnabled === 'false');
      let reason = '';
      if (this.motionQuery?.matches) reason = 'reduced';
      else if (this.contrastQuery?.matches) reason = 'contrast';
      else if (this.dataset.motion === 'false') reason = 'disabled';
      else if (!this.motionSupported) reason = 'unsupported';
      else if (this.dataset.fallback === 'true' || this.mobileFailed || this.dataset.videoFailure === 'true') reason = 'fallback';
      else if (!image?.complete || !image.naturalWidth) reason = 'loading';
      else if (this.motionFailed) reason = 'unavailable_effects';
      else if (intentionallyStillMobile) reason = 'disabled';
      else if (this.motionEmpty) reason = 'unavailable_effects';
      const idle = !reason && !motionPaused && !active;
      this.motionButton.disabled = Boolean(reason || idle);
      this.motionButton.textContent = reason ? labels.unavailable : idle ? labels.idle_button : motionPaused ? labels.play : labels.pause;
      this.motionButton.setAttribute('aria-label', this.motionButton.textContent);
      this.motionButton.setAttribute('title', this.motionButton.textContent);
      this.motionStatus.textContent = reason ? labels[reason] : motionPaused ? labels.paused : active ? labels.playing : labels.idle;
      this.motionControl.dataset.reason = reason || (motionPaused ? 'paused' : active ? 'playing' : 'idle');
      this.motionControl.hidden = false;
    }
    pause() {
      if (this.videoMotion) this.videoMotion.suspended = true;
      this.videoMotion?.pause();
      for (const animation of this.animationHandles || []) { try { animation.pause(); } catch { } }
      this.dataset.running = 'false';
      this.updateMotionControl(this.querySelector('[data-living-frame] img'), false);
    }
  }
  class HomeDinerArt extends HTMLElement {
    connectedCallback() {
      if (this.subscribed) return;
      this.room = this.closest('ggx-portal-scene');
      if (!this.room) return;
      this.subscribed = true;
      subscribe(this);
    }
    refresh() {
      if (!this.isConnected || !this.room) return;
      const state = core.requestedState(this);
      this.room.querySelectorAll('[data-diner-day-srcset]').forEach(source => {
        const value = state === 'night' ? source.dataset.dinerNightSrcset : source.dataset.dinerDaySrcset;
        if (value && source.getAttribute('srcset') !== value) source.setAttribute('srcset', value);
      });
      this.room.querySelectorAll('[data-diner-day-source]').forEach(image => {
        const value = state === 'night' ? image.dataset.dinerNightSource : image.dataset.dinerDaySource;
        if (!value) return;
        if (image.hasAttribute('data-source')) image.dataset.source = value;
        if (image.hasAttribute('href') && image.getAttribute('href') !== value) image.setAttribute('href', value);
      });
      this.dataset.state = state;
    }
    updateMotion() {}
    pause() {}
    disconnectedCallback() {
      if (this.subscribed) unsubscribe(this);
      this.subscribed = false;
      this.room = null;
    }
  }
  customElements.define('ggx-home-diner-art', HomeDinerArt);
  customElements.define('ggx-living-world', LivingWorld);
})();


